import React, { useState, useEffect, useContext } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { FaSearch, FaFilter, FaDownload, FaPrint, FaEye } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const TyreSerialNoReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [tyreSerialNo, setTyreSerialNo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [vehicleNo, setVehicleNo] = useState("");
  const [tyreSize, setTyreSize] = useState("");
  const [brand, setBrand] = useState("");
  const [pattern, setPattern] = useState("");
  const [status, setStatus] = useState("");
  const [position, setPosition] = useState("");

  const [data, setData] = useState([]);

  const navigate = useNavigate();

  const handleSearch = () => {
    setLoading(true);
    const payload = {
      tyreSerialNo,
      vehicleNo,
      tyreSize,
      brand,
      pattern,
      status,
      position
    };

    // Simulate API call - replace with actual endpoint
    setTimeout(() => {
      // Mock data for demonstration
      const mockData = [
        {
          id: 1,
          serialNo: "TSN001",
          vehicleNo: "KA01AB1234",
          tyreSize: "215/75R17.5",
          brand: "Michelin",
          pattern: "XZY-2",
          status: "Active",
          position: "Front Left",
          purchaseDate: "2024-01-15",
          lastService: "2024-03-20",
          kmRun: "15,234"
        },
        {
          id: 2,
          serialNo: "TSN002",
          vehicleNo: "KA02CD5678",
          tyreSize: "225/70R19.5",
          brand: "Bridgestone",
          pattern: "R250",
          status: "In Service",
          position: "Rear Right",
          purchaseDate: "2024-02-10",
          lastService: "2024-03-25",
          kmRun: "12,567"
        }
      ];
      
      setData(tyreSerialNo ? mockData.filter(item => 
        item.serialNo.includes(tyreSerialNo)
      ) : mockData);
      setLoading(false);
    }, 1000);
  };

  const handleViewDetails = (serialNo) => {
    navigate(`/tyre-details/${serialNo}`);
  };

  const handleExport = () => {
    toast.success("Export functionality will be implemented");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="TYRE SERIAL NUMBER TRACKING" />
      <ToastContainer />
      
      <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark 
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" 
          : `bg-white ${currentSkin.glass} text-black`
      }`}>
        
        {/* Search Header */}
        <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between">
          <div className="flex-1 flex gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Enter Tyre Serial Number..."
                value={tyreSerialNo}
                onChange={(e) => setTyreSerialNo(e.target.value.toUpperCase())}
                className="w-full border border-gray-300 px-4 py-3 pl-10 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            
            <button
              onClick={handleSearch}
              disabled={loading}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50`}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaSearch />
              )}
              Search
            </button>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-colors"
            >
              <FaFilter /> Filters
            </button>
            
            <button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaDownload /> Export
            </button>
            
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaPrint /> Print
            </button>
          </div>
        </div>

        {/* Filters Section */}
        {showFilters && (
          <div className={`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4 mb-6 rounded-lg ${
            isDark ? "bg-gray-800/50" : "bg-gray-100"
          }`}>
            <div>
              <label className="block text-sm font-medium mb-2">Vehicle No</label>
              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
                placeholder="Enter vehicle number"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Tyre Size</label>
              <input
                type="text"
                value={tyreSize}
                onChange={(e) => setTyreSize(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
                placeholder="Tyre size"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Brand</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
                placeholder="Brand name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Pattern</label>
              <input
                type="text"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
                placeholder="Pattern type"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="In Service">In Service</option>
                <option value="Retired">Retired</option>
                <option value="Damaged">Damaged</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Position</label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-md"
              >
                <option value="">All Positions</option>
                <option value="Front Left">Front Left</option>
                <option value="Front Right">Front Right</option>
                <option value="Rear Left">Rear Left</option>
                <option value="Rear Right">Rear Right</option>
                <option value="Spare">Spare</option>
              </select>
            </div>
          </div>
        )}

        {/* Results Grid */}
        <div className="mt-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Searching tyre records...</p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FaSearch className="text-4xl mx-auto mb-4 text-gray-400" />
              <p className="text-lg">No tyre records found</p>
              <p className="text-sm">Try adjusting your search criteria</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg shadow">
              <table className="w-full border-collapse">
                <thead className={`bg-${baseColor}-${baseShade} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left">Serial No</th>
                    <th className="px-4 py-3 text-left">Vehicle No</th>
                    <th className="px-4 py-3 text-left">Tyre Size</th>
                    <th className="px-4 py-3 text-left">Brand</th>
                    <th className="px-4 py-3 text-left">Pattern</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Position</th>
                    <th className="px-4 py-3 text-left">KM Run</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 font-semibold">{item.serialNo}</td>
                      <td className="px-4 py-3">{item.vehicleNo}</td>
                      <td className="px-4 py-3">{item.tyreSize}</td>
                      <td className="px-4 py-3">{item.brand}</td>
                      <td className="px-4 py-3">{item.pattern}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          item.status === 'Active' ? 'bg-green-100 text-green-800' :
                          item.status === 'In Service' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{item.position}</td>
                      <td className="px-4 py-3">{item.kmRun}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleViewDetails(item.serialNo)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {data.length > 0 && (
          <div className={`mt-6 p-4 rounded-lg ${
            isDark ? "bg-gray-800/50" : "bg-gray-100"
          }`}>
            <div className="flex flex-wrap gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{data.length}</div>
                <div className="text-sm text-gray-600">Total Tyres</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {data.filter(d => d.status === 'Active').length}
                </div>
                <div className="text-sm text-gray-600">Active</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {data.filter(d => d.status === 'In Service').length}
                </div>
                <div className="text-sm text-gray-600">In Service</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default TyreSerialNoReport;